// SPDX-License-Identifier: BUSL-1.1

/**
 * Reusable skeleton loaders for page layouts.
 *
 * Each skeleton matches the visual structure of its corresponding page
 * so the loading state feels like the real content is about to appear.
 *
 * Usage: wire to React Query's isLoading in each route.
 *
 */

import { Skeleton } from '@/components/ui/skeleton';

/* ── Reusable primitives ─────────────────────────────── */

/** Table skeleton with configurable rows and columns. */
export function TableSkeleton({
  rows = 5,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800">
      {/* Header row */}
      <div className="flex gap-4 border-b border-neutral-800 bg-neutral-900 px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={`h-${i}`} className="h-4 flex-1" />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={`r-${r}`}
          className="flex gap-4 border-b border-neutral-800/50 px-4 py-3 last:border-b-0"
        >
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={`c-${r}-${c}`} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Card skeleton matching the dashboard summary cards. */
function CardSkeleton() {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <Skeleton className="mb-2 h-4 w-24" />
      <Skeleton className="mb-2 h-7 w-16" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

/* ── Page-specific skeletons ─────────────────────────── */

/** Dashboard: 4 summary cards + activity table. */
export function DashboardSkeleton() {
  return (
    <div>
      <Skeleton className="mb-6 h-8 w-36" />
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
      <Skeleton className="mb-4 h-6 w-40" />
      <TableSkeleton rows={5} cols={4} />
    </div>
  );
}

/** Config list: heading + table. */
export function ConfigListSkeleton() {
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-9 w-36 rounded" />
      </div>
      <TableSkeleton rows={6} cols={4} />
    </div>
  );
}

/** Config editor: sidebar version list + editor panel. */
export function ConfigEditorSkeleton() {
  return (
    <div className="flex gap-6">
      {/* Version sidebar */}
      <div className="w-64 space-y-3">
        <Skeleton className="h-6 w-32" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded border border-neutral-800 p-3">
            <Skeleton className="mb-2 h-4 w-20" />
            <Skeleton className="h-3 w-28" />
          </div>
        ))}
      </div>
      {/* Editor panel */}
      <div className="flex-1 space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24 rounded" />
            <Skeleton className="h-9 w-24 rounded" />
          </div>
        </div>
        <Skeleton className="h-64 w-full rounded" />
      </div>
    </div>
  );
}

/** Config diff: two panels side by side. */
export function ConfigDiffSkeleton() {
  return (
    <div>
      <Skeleton className="mb-6 h-8 w-48" />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-48 w-full rounded" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-48 w-full rounded" />
        </div>
      </div>
    </div>
  );
}

/** Flags page: heading + table. */
export function FlagListSkeleton() {
  return (
    <div>
      <Skeleton className="mb-6 h-8 w-36" />
      <TableSkeleton rows={5} cols={4} />
    </div>
  );
}

/** Audit log: filters + table. */
export function AuditLogSkeleton() {
  return (
    <div>
      <Skeleton className="mb-6 h-8 w-32" />
      <div className="mb-4 flex gap-3">
        <Skeleton className="h-9 w-36 rounded" />
        <Skeleton className="h-9 w-36 rounded" />
        <Skeleton className="h-9 w-36 rounded" />
      </div>
      <TableSkeleton rows={8} cols={5} />
    </div>
  );
}

/** Agent list: 3 summary cards + table. */
export function AgentListSkeleton() {
  return (
    <div>
      <Skeleton className="mb-6 h-8 w-32" />
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
      <TableSkeleton rows={5} cols={5} />
    </div>
  );
}

/** Moderation inbox: heading + tab bar + item cards. */
export function InboxSkeleton() {
  return (
    <div>
      <div className="mb-4 flex gap-2">
        <Skeleton className="h-8 w-20 rounded" />
        <Skeleton className="h-8 w-20 rounded" />
        <Skeleton className="h-8 w-20 rounded" />
        <Skeleton className="h-8 w-20 rounded" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-neutral-800 bg-neutral-900 p-4"
          >
            <div className="mb-3 flex items-center gap-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="ml-auto h-4 w-16" />
            </div>
            <Skeleton className="mb-2 h-4 w-48" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Agent detail: header + sessions table. */
export function AgentDetailSkeleton() {
  return (
    <div>
      <Skeleton className="mb-2 h-8 w-48" />
      <div className="mb-6 flex gap-3">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-32" />
      </div>
      <Skeleton className="mb-4 h-6 w-28" />
      <TableSkeleton rows={5} cols={6} />
    </div>
  );
}
